import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import PoweredBy from "@/components/PoweredBy";
import { ArrowRight, ShieldCheck, MapPin, Calendar, User, Phone, Mail, Facebook, Globe } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MeetInstructor() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const [bio, setBio] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/instructor-bio`)
      .then((r) => setBio(r.data))
      .catch(() => setBio({}));
  }, []);

  const photoSrc = bio?.photo_url
    ? (bio.photo_url.startsWith("http") ? bio.photo_url : `${API_URL}${bio.photo_url}`)
    : null;

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="meet-brand-link">
            {branding.logo_url
              ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
              : <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <Link to="/services" className="hidden md:inline underline text-muted-foreground hover:text-foreground">Services</Link>
            <Link to="/book" className="hidden md:inline underline text-muted-foreground hover:text-foreground">Book</Link>
            <Link to="/intake" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Client Intake</Link>
            {user
              ? <Button size="sm" onClick={() => window.location.assign(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="meet-nav-dashboard">Go to app</Button>
              : <Button size="sm" asChild data-testid="meet-nav-register"><Link to="/register">Get started</Link></Button>}
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto p-6 lg:p-12 py-16" data-testid="meet-instructor">
        {!bio ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left column — bio text */}
            <div className="order-2 md:order-1">
              <div className="label-caps mb-4">Meet your tax professional</div>
              <h1 className="font-serif text-5xl md:text-6xl leading-none mb-4" data-testid="meet-instructor-name">
                {bio.name}
              </h1>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-medium" data-testid="meet-virtual-badge">
                <Globe size={13} /> 100% virtual · file from anywhere in the US
              </div>
              {bio.title && (
                <div className="text-accent font-medium mb-2" data-testid="meet-instructor-title">{bio.title}</div>
              )}
              {bio.credentials && (
                <div className="flex items-start gap-2 text-sm mb-3">
                  <ShieldCheck size={14} className="mt-0.5 text-primary shrink-0" />
                  <span>{bio.credentials}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-8">
                {bio.location && (
                  <span className="flex items-center gap-1.5"><MapPin size={13} /> {bio.location}</span>
                )}
                {bio.since_year && (
                  <span className="flex items-center gap-1.5"><Calendar size={13} /> Serving clients since {bio.since_year}</span>
                )}
              </div>

              <div className="space-y-4 text-[15px] leading-relaxed max-w-xl" data-testid="meet-instructor-bio">
                {(bio.bio_paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              {bio.tagline && (
                <div className="mt-10 pt-6 border-t border-border font-serif text-2xl md:text-3xl" data-testid="meet-instructor-tagline">
                  {bio.tagline}
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="meet-cta-book"><Link to="/book">Book an appointment <ArrowRight size={14} className="ml-2" /></Link></Button>
                <Button asChild size="lg" variant="outline" data-testid="meet-cta-intake"><Link to="/intake">Start client intake</Link></Button>
              </div>
            </div>

            {/* Right column — photo */}
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-3 rounded-lg bg-primary/10 -z-0" />
                <div className="relative rounded-lg overflow-hidden bg-card border border-border aspect-[3/4] max-w-md mx-auto">
                  {photoSrc ? (
                    <img
                      src={photoSrc}
                      alt={bio.name}
                      className="w-full h-full object-cover"
                      data-testid="meet-instructor-photo"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-muted-foreground p-8 text-center">
                      <div>
                        <User size={40} className="mx-auto mb-3 opacity-40" />
                        <div className="text-sm">
                          Photo coming soon.<br />
                          {user?.role === "admin" && (
                            <Link to="/admin/instructor-bio" className="underline text-primary">Upload one</Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {bio?.footer && (
        <section className="bg-primary text-primary-foreground">
          <div className="max-w-6xl mx-auto px-6 py-8 text-center font-serif text-xl md:text-2xl" data-testid="meet-instructor-footer">
            {bio.footer}
          </div>
        </section>
      )}

      {/* Gallery */}
      {bio?.gallery_urls?.length > 0 && (
        <section className="border-t border-border" data-testid="meet-gallery">
          <div className="max-w-6xl mx-auto p-6 lg:p-12 py-16">
            <div className="mb-8">
              <div className="label-caps mb-2">Around the office</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">Come see us work.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bio.gallery_urls.map((url, i) => {
                const src = url.startsWith("http") ? url : `${API_URL}${url}`;
                return (
                  <a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden bg-secondary/30 border border-border aspect-[4/5] group"
                    data-testid={`meet-gallery-photo-${i}`}
                  >
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Contact strip */}
      {(branding?.phone || branding?.email || branding?.facebook || branding?.address || branding?.website) && (
        <section className="border-t border-border bg-card" data-testid="meet-contact">
          <div className="max-w-6xl mx-auto p-6 lg:p-12 py-14">
            <div className="mb-8">
              <div className="label-caps mb-2">Get in touch</div>
              <h2 className="font-serif text-3xl md:text-4xl">Ready when you are.</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {branding.phone && (
                <ContactTile
                  href={`tel:${branding.phone.replace(/[^0-9+]/g, "")}`}
                  icon={<Phone size={16} />}
                  label="Call"
                  value={branding.phone}
                  testid="meet-contact-phone"
                />
              )}
              {branding.email && (
                <ContactTile
                  href={`mailto:${branding.email}`}
                  icon={<Mail size={16} />}
                  label="Email"
                  value={branding.email}
                  testid="meet-contact-email"
                />
              )}
              {branding.facebook && (
                <ContactTile
                  href={branding.facebook.startsWith("http") ? branding.facebook : `https://${branding.facebook}`}
                  target="_blank"
                  icon={<Facebook size={16} />}
                  label="Facebook"
                  value={
                    branding.facebook
                      .replace(/^https?:\/\/(www\.)?/, "")
                      .replace(/^facebook\.com\//, "")
                      .replace(/\/$/, "") || "DyNamic Tax"
                  }
                  testid="meet-contact-facebook"
                />
              )}
              {branding.address && (
                <ContactTile
                  href={`https://maps.google.com/?q=${encodeURIComponent(branding.address)}`}
                  target="_blank"
                  icon={<MapPin size={16} />}
                  label="Visit"
                  value={branding.address}
                  testid="meet-contact-address"
                />
              )}
              {branding.website && (
                <ContactTile
                  href={branding.website.startsWith("http") ? branding.website : `https://${branding.website}`}
                  target="_blank"
                  icon={<Globe size={16} />}
                  label="Website"
                  value={branding.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  testid="meet-contact-website"
                />
              )}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <PoweredBy className="!m-0" />
          <div className="flex gap-4 flex-wrap">
            <Link to="/" className="underline hover:text-foreground">Home</Link>
            <Link to="/services" className="underline hover:text-foreground">Services</Link>
            <Link to="/book" className="underline hover:text-foreground">Book</Link>
            <Link to="/intake" className="underline hover:text-foreground">Client intake</Link>
            <Link to="/client-portal" className="underline hover:text-foreground">Client portal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const ContactTile = ({ href, target, icon, label, value, testid }) => (
  <a
    href={href}
    target={target}
    rel={target === "_blank" ? "noopener noreferrer" : undefined}
    className="border border-border rounded-md p-4 bg-background hover:border-primary transition-colors flex items-center gap-3 group"
    data-testid={testid}
  >
    <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="label-caps text-[10px]">{label}</div>
      <div className="text-sm truncate">{value}</div>
    </div>
  </a>
);
